<script setup>
import { ref, computed } from 'vue'

const props = defineProps({
  show: Boolean
})

const emit = defineEmits(['close'])

const activeCategory = ref('competition')
const selectedExercise = ref(null)

const exerciseCategories = [
  {
    id: 'main',
    label: 'Main Lifts',
    icon: 'fa-dumbbell',
    exercises: [
      { name: 'Squat', unit: 'kg' },
      { name: 'Bench Press', unit: 'kg' },
      { name: 'Deadlift', unit: 'kg' }
    ]
  },
  {
    id: 'squat',
    label: 'Squat Movements',
    icon: 'fa-dumbbell',
    exercises: [
      { name: 'Pause Squat', unit: 'kg' },
      { name: 'Front Squat', unit: 'kg' },
      { name: 'Box Squat', unit: 'kg' },
      { name: 'Safety Bar Squat', unit: 'kg' },
      { name: 'Hack Squat', unit: 'kg' },
      { name: 'Belt Squat', unit: 'kg' },
      { name: 'Leg Press', unit: 'kg' },
      { name: 'Bulgarian Split Squat', unit: 'kg' },
      { name: 'Walking Lunges', unit: 'kg' }
    ]
  },
  {
    id: 'bench',
    label: 'Bench Movements',
    icon: 'fa-bench-press',
    exercises: [
      { name: 'Close Grip Bench Press', unit: 'kg' },
      { name: 'Pause Bench Press', unit: 'kg' },
      { name: 'Spoto Press', unit: 'kg' },
      { name: 'Floor Press', unit: 'kg' },
      { name: 'Incline Bench Press', unit: 'kg' },
      { name: 'Dumbbell Bench Press', unit: 'kg' },
      { name: 'Overhead Press', unit: 'kg' },
      { name: 'Dips', unit: 'kg' },
      { name: 'Tricep Extensions', unit: 'kg' },
      { name: 'JM Press', unit: 'kg' }
    ]
  },
  {
    id: 'deadlift',
    label: 'Deadlift Movements',
    icon: 'fa-weight-hanging',
    exercises: [
      { name: 'Deficit Deadlift', unit: 'kg' },
      { name: 'Pause Deadlift', unit: 'kg' },
      { name: 'Romanian Deadlift', unit: 'kg' },
      { name: 'Stiff Leg Deadlift', unit: 'kg' },
      { name: 'Block Pull', unit: 'kg' },
      { name: 'Good Morning', unit: 'kg' },
      { name: 'Back Extension', unit: 'kg' },
      { name: 'Barbell Row', unit: 'kg' },
      { name: 'Pull-ups', unit: 'reps' }
    ]
  },
  {
    id: 'core',
    label: 'Core Work',
    icon: 'fa-person',
    exercises: [
      { name: 'Ab Wheel', unit: 'reps' },
      { name: 'Plank', unit: 'time' },
      { name: 'Cable Crunch', unit: 'kg' },
      { name: 'Hanging Leg Raise', unit: 'reps' }
    ]
  }
]

// Sample historical data for deadlift
const deadliftHistory = [
  {
    date: '2024-03-20T10:30:00.000Z',
    sets: [
      { id: 1, exercise: 'Deadlift', weight: 180, reps: 2, total: 360, notes: 'PR attempt', date: '2024-03-20T10:30:00.000Z' },
      { id: 2, exercise: 'Deadlift', weight: 160, reps: 3, total: 480, notes: 'Back off set', date: '2024-03-20T10:35:00.000Z' },
      { id: 3, exercise: 'Deadlift', weight: 140, reps: 5, total: 700, notes: 'Volume work', date: '2024-03-20T10:40:00.000Z' }
    ]
  },
  {
    date: '2024-03-18T10:30:00.000Z',
    sets: [
      { id: 4, exercise: 'Deadlift', weight: 150, reps: 5, total: 750, notes: 'Speed work', date: '2024-03-18T10:30:00.000Z' },
      { id: 5, exercise: 'Deadlift', weight: 150, reps: 5, total: 750, notes: 'Form feels good', date: '2024-03-18T10:35:00.000Z' },
      { id: 6, exercise: 'Deadlift', weight: 150, reps: 5, total: 750, notes: 'Last set', date: '2024-03-18T10:40:00.000Z' }
    ]
  },
  {
    date: '2024-03-15T10:30:00.000Z',
    sets: [
      { id: 7, exercise: 'Deadlift', weight: 170, reps: 3, total: 510, notes: 'Heavy singles', date: '2024-03-15T10:30:00.000Z' },
      { id: 8, exercise: 'Deadlift', weight: 170, reps: 3, total: 510, notes: 'Good bar speed', date: '2024-03-15T10:35:00.000Z' }
    ]
  },
  {
    date: '2024-03-12T10:30:00.000Z',
    sets: [
      { id: 9, exercise: 'Deadlift', weight: 140, reps: 8, total: 1120, notes: 'Volume day', date: '2024-03-12T10:30:00.000Z' },
      { id: 10, exercise: 'Deadlift', weight: 140, reps: 8, total: 1120, notes: 'Building work capacity', date: '2024-03-12T10:35:00.000Z' },
      { id: 11, exercise: 'Deadlift', weight: 140, reps: 8, total: 1120, notes: 'Last set RPE 8', date: '2024-03-12T10:40:00.000Z' }
    ]
  }
]

// Modify the workoutData initialization to include deadlift history
const workoutData = ref({})
exerciseCategories.forEach(category => {
  category.exercises.forEach(exercise => {
    if (exercise.name === 'Deadlift') {
      // Initialize deadlift with historical data
      workoutData.value[exercise.name] = deadliftHistory.flatMap(day => day.sets)
    } else {
      // Initialize other exercises with empty arrays
      workoutData.value[exercise.name] = []
    }
  })
})

const newSet = ref({
  weight: '',
  reps: '',
  notes: '',
  video: null,
  videoUrl: '',
  total: computed(() => {
    const weight = Number(newSet.value.weight) || 0
    const reps = Number(newSet.value.reps) || 0
    return weight * reps
  })
})

const handleVideoUpload = (event) => {
  const file = event.target.files[0]
  if (file) {
    newSet.value.video = file
    newSet.value.videoUrl = URL.createObjectURL(file)
  }
}

const formatDate = (dateString) => {
  const date = new Date(dateString)
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = date.getFullYear()
  return `${day}.${month}.${year}`
}

const addSet = () => {
  if (!selectedExercise.value) {
    console.warn('No exercise selected')
    return
  }

  if (!newSet.value.weight || !newSet.value.reps) {
    console.warn('Weight and reps are required')
    return
  }

  const set = {
    id: Date.now(),
    exercise: selectedExercise.value.name,
    weight: Number(newSet.value.weight),
    reps: Number(newSet.value.reps),
    notes: newSet.value.notes,
    video: newSet.value.video,
    videoUrl: newSet.value.videoUrl,
    total: newSet.value.total,
    date: new Date().toISOString()
  }

  // Initialize array if it doesn't exist
  if (!workoutData.value[selectedExercise.value.name]) {
    workoutData.value[selectedExercise.value.name] = []
  }

  // Add the new set
  workoutData.value[selectedExercise.value.name].push(set)

  // Reset form
  newSet.value = {
    weight: '',
    reps: '',
    notes: '',
    video: null,
    videoUrl: '',
    total: computed(() => {
      const weight = Number(newSet.value.weight) || 0
      const reps = Number(newSet.value.reps) || 0
      return weight * reps
    })
  }
}

const deleteSet = (setId) => {
  if (!selectedExercise.value?.name) return

  workoutData.value[selectedExercise.value.name] = 
    workoutData.value[selectedExercise.value.name].filter(set => set.id !== setId)
}

const currentExercises = computed(() => {
  return exerciseCategories.find(cat => cat.id === activeCategory.value)?.exercises || []
})

const calculateOneRM = (weight, reps) => {
  // Brzycki Formula
  return Math.round((weight * (36 / (37 - reps))) * 100) / 100
}

const getExerciseStats = (exercise) => {
  const sets = workoutData.value[exercise].filter(
    set => set.exercise === exercise
  )
  
  if (!sets.length) return null

  const maxWeight = Math.max(...sets.map(set => Number(set.weight)))
  const maxReps = Math.max(...sets.map(set => Number(set.reps)))
  const estimatedOneRM = Math.max(
    ...sets.map(set => calculateOneRM(Number(set.weight), Number(set.reps)))
  )

  return {
    maxWeight,
    maxReps,
    estimatedOneRM
  }
}

// Add search functionality
const searchQuery = ref('')

// Add computed property for filtered exercises
const filteredExercises = computed(() => {
  const query = searchQuery.value.toLowerCase()
  return exerciseCategories.map(category => ({
    ...category,
    exercises: category.exercises.filter(exercise => 
      exercise.name.toLowerCase().includes(query)
    )
  })).filter(category => category.exercises.length > 0)
})

// Update the groupedSets computed property
const groupedSets = computed(() => {
  if (!selectedExercise.value) return []
  
  const sets = workoutData.value[selectedExercise.value.name] || []
  
  // Group by date (using only the date part)
  const grouped = sets.reduce((acc, set) => {
    const dateOnly = set.date.split('T')[0]
    if (!acc[dateOnly]) {
      acc[dateOnly] = {
        date: dateOnly,
        sets: [],
        totalVolume: 0,
        maxWeight: 0,
        totalSets: 0
      }
    }
    
    acc[dateOnly].sets.push(set)
    acc[dateOnly].totalVolume += set.total
    acc[dateOnly].maxWeight = Math.max(acc[dateOnly].maxWeight, set.weight)
    acc[dateOnly].totalSets += 1
    
    return acc
  }, {})

  // Convert to array and sort by date
  return Object.values(grouped)
    .sort((a, b) => new Date(b.date) - new Date(a.date))
})

// Add this with your other refs
const selectedVideo = ref(null)

// Add new refs and computed properties
const viewMode = ref('exercise') // 'exercise' or 'daily'
const planStartDate = ref('')
const planEndDate = ref('')
const planStartWeight = ref(0)
const planGoalWeight = ref(0)
const planWorkouts = ref(0)
const generatedPlan = ref([])

// Computed property for daily workouts
const dailyWorkouts = computed(() => {
  // Get all sets from all exercises
  const allSets = Object.entries(workoutData.value).flatMap(([exerciseName, sets]) => 
    sets.map(set => ({
      ...set,
      exerciseName
    }))
  )

  // Group by date (using only the date part of the timestamp)
  const grouped = allSets.reduce((acc, set) => {
    const dateOnly = set.date.split('T')[0] // Get just the date part
    if (!acc[dateOnly]) {
      acc[dateOnly] = {
        date: set.date,
        exercises: {},
        totalVolume: 0,
        totalSets: 0
      }
    }
    
    if (!acc[dateOnly].exercises[set.exerciseName]) {
      acc[dateOnly].exercises[set.exerciseName] = []
    }
    
    acc[dateOnly].exercises[set.exerciseName].push(set)
    acc[dateOnly].totalVolume += set.total
    acc[dateOnly].totalSets += 1
    
    return acc
  }, {})

  // Convert to array and sort by date (most recent first)
  return Object.values(grouped)
    .sort((a, b) => new Date(b.date) - new Date(a.date))
})

// Add Quick Add functionality
const quickAddExercise = ref(null)

const quickAdd = (exerciseName) => {
  quickAddExercise.value = exerciseName
  // Reset form
  newSet.value = {
    weight: '',
    reps: '',
    notes: '',
    video: null,
    videoUrl: '',
    total: computed(() => {
      const weight = Number(newSet.value.weight) || 0
      const reps = Number(newSet.value.reps) || 0
      return weight * reps
    })
  }
}

const addQuickSet = (exerciseName) => {
  if (!newSet.value.weight || !newSet.value.reps) return

  const set = {
    id: Date.now(),
    exercise: exerciseName,
    weight: Number(newSet.value.weight),
    reps: Number(newSet.value.reps),
    notes: newSet.value.notes,
    video: null,
    videoUrl: '',
    total: newSet.value.total,
    date: new Date().toISOString()
  }

  // Initialize array if it doesn't exist
  if (!workoutData.value[exerciseName]) {
    workoutData.value[exerciseName] = []
  }

  // Add the new set
  workoutData.value[exerciseName].push(set)

  // Reset form and close quick add
  quickAddExercise.value = null
  newSet.value = {
    weight: '',
    reps: '',
    notes: '',
    video: null,
    videoUrl: '',
    total: computed(() => {
      const weight = Number(newSet.value.weight) || 0
      const reps = Number(newSet.value.reps) || 0
      return weight * reps
    })
  }
}

const generateWorkoutPlan = () => {
  if (!planStartDate.value || !planEndDate.value || 
      !planStartWeight.value || !planGoalWeight.value || 
      !planWorkouts.value || !selectedExercise.value) {
    console.log('Missing required fields:', {
      startDate: planStartDate.value,
      endDate: planEndDate.value,
      startWeight: planStartWeight.value,
      goalWeight: planGoalWeight.value,
      workouts: planWorkouts.value,
      exercise: selectedExercise.value
    })
    return
  }

  try {
    const startDate = new Date(planStartDate.value)
    const endDate = new Date(planEndDate.value)
    const totalDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24))
    const daysInterval = Math.floor(totalDays / (Number(planWorkouts.value) - 1))
    
    const weightDiff = Number(planGoalWeight.value) - Number(planStartWeight.value)
    const weightIncrement = weightDiff / (Number(planWorkouts.value) - 1)

    generatedPlan.value = []
    
    for (let i = 0; i < Number(planWorkouts.value); i++) {
      const workoutDate = new Date(startDate)
      workoutDate.setDate(startDate.getDate() + (i * daysInterval))
      
      const plannedWeight = Math.round((Number(planStartWeight.value) + (weightIncrement * i)) * 10) / 10

      generatedPlan.value.push({
        date: workoutDate.toISOString().split('T')[0],
        weight: plannedWeight,
        exercise: selectedExercise.value.name
      })
    }

    console.log('Generated plan:', generatedPlan.value)
  } catch (error) {
    console.error('Error generating plan:', error)
  }
}

const generateProgressPath = () => {
  if (!generatedPlan.value.length) return ''

  const points = generateDataPoints()
  return `M ${points.map(p => `${p.x},${p.y}`).join(' L ')}`
}

const generateDataPoints = () => {
  if (!generatedPlan.value.length) return []

  const startWeight = Number(planStartWeight.value)
  const endWeight = Number(planGoalWeight.value)
  const weightRange = endWeight - startWeight

  return generatedPlan.value.map((workout, index) => {
    const x = (index / (generatedPlan.value.length - 1)) * 100 + '%'
    const normalizedWeight = (workout.weight - startWeight) / weightRange
    const y = (1 - normalizedWeight) * 100 + '%'
    
    return { x, y }
  })
}

const handleGeneratePlan = () => {
  console.log('Generate button clicked')
  console.log('Current values:', {
    exercise: selectedExercise.value,
    startDate: planStartDate.value,
    endDate: planEndDate.value,
    startWeight: planStartWeight.value,
    goalWeight: planGoalWeight.value,
    workouts: planWorkouts.value
  })

  if (!selectedExercise.value) {
    console.log('No exercise selected')
    return
  }

  if (!planStartDate.value || !planEndDate.value || 
      !planStartWeight.value || !planGoalWeight.value || 
      !planWorkouts.value) {
    console.log('Missing required fields')
    return
  }

  generateWorkoutPlan()
}
</script>

<template>
  <transition
    enter-active-class="transition-opacity duration-300"
    leave-active-class="transition-opacity duration-300"
    enter-from-class="opacity-0"
    enter-to-class="opacity-100"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0"
  >
    <div v-if="show" class="fixed inset-0 bg-black bg-opacity-50 z-50 p-8">
      <div class="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full h-full flex">
        <!-- Left Sidebar -->
        <div class="w-80 border-r dark:border-gray-700 flex flex-col">
          <!-- Date Selector -->
          <div class="p-4 border-b dark:border-gray-700">
            <input 
              type="date" 
              v-model="selectedDate"
              class="w-full p-2 rounded-lg border dark:bg-gray-800 dark:border-gray-700 dark:text-white"
            >
          </div>

          <!-- Add search input above categories -->
          <div class="p-4 border-b dark:border-gray-700">
            <div class="relative">
              <input 
                v-model="searchQuery"
                type="text"
                placeholder="Search exercises..."
                class="w-full pl-10 pr-4 py-2 rounded-lg border dark:bg-gray-800 dark:border-gray-700 dark:text-white"
              >
              <i class="fas fa-search absolute left-3 top-3 text-gray-400"></i>
            </div>
          </div>

          <!-- Exercise Categories -->
          <div class="flex-1 overflow-y-auto">
            <div v-for="category in filteredExercises" :key="category.id">
              <!-- Category Header -->
              <div 
                class="p-4 bg-gray-50 dark:bg-gray-800 border-b dark:border-gray-700 font-medium text-gray-700 dark:text-gray-300"
              >
                <i :class="['fas', category.icon, 'mr-2']"></i>
                {{ category.label }}
              </div>
              
              <!-- Exercises List -->
              <button
                v-for="exercise in category.exercises"
                :key="exercise.name"
                @click="selectedExercise = exercise; activeCategory = category.id"
                class="w-full p-4 flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors relative border-b dark:border-gray-700"
                :class="selectedExercise?.name === exercise.name ? 'bg-gray-100 dark:bg-gray-800' : ''"
              >
                <div class="flex-1 text-left">
                  <div class="font-medium dark:text-white">{{ exercise.name }}</div>
                  <div class="text-sm text-gray-500">
                    {{ workoutData[exercise.name]?.length || 0 }} sets recorded
                  </div>
                </div>

                <!-- Show max weight if available -->
                <div v-if="getExerciseStats(exercise.name)" class="text-sm text-gray-500">
                  {{ getExerciseStats(exercise.name).maxWeight }}kg
                </div>

                <!-- Active Indicator -->
                <div 
                  v-if="selectedExercise?.name === exercise.name"
                  class="absolute left-0 top-0 bottom-0 w-1 bg-pink-500"
                ></div>
              </button>
            </div>
          </div>
        </div>

        <!-- Main Content Area -->
        <div class="flex-1 flex flex-col">
          <!-- Header -->
          <div class="flex items-center justify-between p-6 border-b dark:border-gray-700">
            <div class="flex items-center gap-4">
              <h2 class="text-2xl font-bold dark:text-white">
                {{ selectedExercise?.name || 'Select an Exercise' }}
              </h2>
            </div>
            <button 
              @click="$emit('close')"
              class="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
            >
              <i class="fas fa-times text-xl"></i>
            </button>
          </div>

          <!-- View Mode Selector -->
          <div class="flex items-center justify-end px-6 py-2 border-b dark:border-gray-700 bg-white dark:bg-gray-900">
            <div class="flex rounded-lg overflow-hidden border dark:border-gray-700">
              <button
                @click="viewMode = 'exercise'"
                class="px-4 py-2 text-sm font-medium transition-colors"
                :class="viewMode === 'exercise' ? 'bg-pink-500 text-white' : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50'"
              >
                Exercise View
              </button>
              <button
                @click="viewMode = 'daily'"
                class="px-4 py-2 text-sm font-medium transition-colors"
                :class="viewMode === 'daily' ? 'bg-pink-500 text-white' : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50'"
              >
                Daily Summary
              </button>
              <button
                @click="viewMode = 'plan'"
                class="px-4 py-2 text-sm font-medium transition-colors"
                :class="viewMode === 'plan' ? 'bg-pink-500 text-white' : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50'"
              >
                Plan Generator
              </button>
            </div>
          </div>

          <!-- Content Area -->
          <div class="flex-1 overflow-y-auto">
            <!-- Exercise View -->
            <div v-if="viewMode === 'exercise'" class="p-6">
              <!-- Existing exercise view content -->
            </div>

            <!-- Daily View -->
            <div v-else-if="viewMode === 'daily'" class="p-6">
              <!-- Existing daily view content -->
            </div>

            <!-- Plan Generator View -->
            <div v-else-if="viewMode === 'plan'" class="p-6">
              <div class="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
                <h3 class="text-xl font-bold mb-6 dark:text-white">Generate Workout Plan</h3>
                
                <!-- Plan Generator Form -->
                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Start Date
                    </label>
                    <input 
                      type="date" 
                      v-model="planStartDate"
                      class="w-full p-2 rounded-lg border dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    >
                  </div>
                  
                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      End Date
                    </label>
                    <input 
                      type="date" 
                      v-model="planEndDate"
                      class="w-full p-2 rounded-lg border dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    >
                  </div>
                  
                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Number of Workouts
                    </label>
                    <input 
                      type="number" 
                      v-model.number="planWorkouts"
                      min="2"
                      step="1"
                      class="w-full p-2 rounded-lg border dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    >
                  </div>
                  
                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Starting Weight ({{ selectedExercise?.unit || 'kg' }})
                    </label>
                    <input 
                      type="number" 
                      v-model.number="planStartWeight"
                      step="0.5"
                      class="w-full p-2 rounded-lg border dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    >
                  </div>
                  
                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Goal Weight ({{ selectedExercise?.unit || 'kg' }})
                    </label>
                    <input 
                      type="number" 
                      v-model.number="planGoalWeight"
                      step="0.5"
                      class="w-full p-2 rounded-lg border dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    >
                  </div>
                </div>

                <button
                  @click="handleGeneratePlan"
                  class="px-6 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition-colors"
                >
                  Generate Plan
                </button>

                <!-- Generated Plan Table -->
                <div v-if="generatedPlan.length" class="mt-8">
                  <h4 class="text-lg font-semibold mb-4 dark:text-white">Generated Plan for {{ selectedExercise?.name }}</h4>
                  <div class="overflow-x-auto">
                    <table class="min-w-full">
                      <thead>
                        <tr class="text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                          <th class="px-4 py-2">Date</th>
                          <th class="px-4 py-2">Weight ({{ selectedExercise?.unit || 'kg' }})</th>
                        </tr>
                      </thead>
                      <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
                        <tr v-for="workout in generatedPlan" :key="workout.date" class="text-sm">
                          <td class="px-4 py-3">{{ formatDate(workout.date) }}</td>
                          <td class="px-4 py-3 font-medium">{{ workout.weight }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <!-- Generated Plan Grid -->
                <div v-if="generatedPlan.length" class="mt-8">
                  <h4 class="text-lg font-semibold mb-4 dark:text-white">Progress Grid</h4>
                  <div class="relative">
                    <!-- Weight Labels (Y-axis) -->
                    <div class="absolute -left-16 top-0 bottom-0 w-14 flex flex-col justify-between text-sm text-gray-500">
                      <div v-for="weight in [planGoalWeight, 
                        Math.round((Number(planGoalWeight) + Number(planStartWeight)) / 2), 
                        planStartWeight]" 
                        :key="weight"
                        class="text-right"
                      >
                        {{ weight }}{{ selectedExercise?.unit }}
                      </div>
                    </div>

                    <!-- Grid -->
                    <div class="ml-0 p-4 bg-white dark:bg-gray-800 rounded-lg">
                      <div class="relative h-60 border-l border-b border-gray-300 dark:border-gray-600">
                        <!-- Grid Lines -->
                        <div class="absolute inset-0">
                          <div class="grid grid-cols-1 h-full">
                            <div class="border-t border-gray-200 dark:border-gray-700"></div>
                            <div class="border-t border-gray-200 dark:border-gray-700"></div>
                            <div class="border-t border-gray-200 dark:border-gray-700"></div>
                          </div>
                        </div>

                        <!-- Progress Line -->
                        <div class="absolute inset-0">
                          <svg class="w-full h-full">
                            <path
                              :d="generateProgressPath()"
                              class="stroke-pink-500 stroke-2 fill-none"
                            />
                            <!-- Data Points -->
                            <circle
                              v-for="(point, index) in generateDataPoints()"
                              :key="index"
                              :cx="point.x"
                              :cy="point.y"
                              r="4"
                              class="fill-pink-500"
                            />
                          </svg>
                        </div>

                        <!-- Date Labels (X-axis) -->
                        <div class="absolute -bottom-6 left-0 right-0 flex justify-between text-sm text-gray-500">
                          <div v-for="(workout, index) in [generatedPlan[0], 
                            generatedPlan[Math.floor(generatedPlan.length / 2)], 
                            generatedPlan[generatedPlan.length - 1]]"
                            :key="index"
                            class="text-center"
                          >
                            {{ formatDate(workout.date) }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<style scoped>
/* Add any additional styles here */
</style> 